/**
 * useRevokeUserSession — TanStack mutation pair over the two revoke
 * flavors of the sessions endpoint:
 *
 *  - `revokeOneAsync(sid)` → `DELETE /sessions/{sid}` (204 success).
 *  - `revokeAllAsync()`    → `DELETE /sessions?scope=all` (200 + `{ revoked_count }`).
 *
 * Both invalidate the sessions list AND the audit feed on success
 * because every revoke emits a `user.session_revoked` audit event the
 * UI should surface immediately.
 *
 * Error envelope mirrors phase 2: tagged union `forbidden | notFound |
 * error`. The BE's BLOCK_SELF guard (forbidding actors from revoking
 * their own current session) surfaces as 403 — the page renders the
 * same "permission denied" alert, so we don't carve out a separate
 * kind.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildUserSessionsQueryKey } from './use-user-sessions.js';
import { buildUserAuditQueryKey } from './use-user-audit.js';

export type RevokeSessionError =
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'error' };

export interface UseRevokeUserSessionResult {
  readonly revokeOneAsync: (sid: string) => Promise<void>;
  readonly revokeAllAsync: () => Promise<number>;
  readonly isPendingOne: boolean;
  readonly isPendingAll: boolean;
}

function mapErrorStatus(status: number): RevokeSessionError {
  if (status === 403) return { kind: 'forbidden' };
  if (status === 404) return { kind: 'notFound' };
  return { kind: 'error' };
}

export function useRevokeUserSession(
  userId: string | undefined,
): UseRevokeUserSessionResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: buildUserSessionsQueryKey(orgSlug, userId),
    });
    await queryClient.invalidateQueries({
      queryKey: buildUserAuditQueryKey(orgSlug, userId),
    });
  }

  const revokeOne = useMutation<null, RevokeSessionError, string>({
    mutationFn: async (sid) => {
      if (orgSlug === null || userId === undefined) {
        throw { kind: 'error' } satisfies RevokeSessionError;
      }
      const { response } = await apiClient.DELETE(
        '/organizations/{slug}/users/{userId}/sessions/{sid}',
        {
          params: {
            path: { slug: orgSlug, userId, sid },
          },
        },
      );
      if (!response.ok) throw mapErrorStatus(response.status);
      return null;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const revokeAll = useMutation<number, RevokeSessionError, undefined>({
    mutationFn: async () => {
      if (orgSlug === null || userId === undefined) {
        throw { kind: 'error' } satisfies RevokeSessionError;
      }
      const { response, data } = await apiClient.DELETE(
        '/organizations/{slug}/users/{userId}/sessions',
        {
          params: {
            path: { slug: orgSlug, userId },
            query: { scope: 'all' as const } as never,
          },
        },
      );
      if (!response.ok) throw mapErrorStatus(response.status);
      const count =
        data !== undefined &&
        data !== null &&
        typeof data === 'object' &&
        'revoked_count' in data &&
        typeof (data as { revoked_count: unknown }).revoked_count === 'number'
          ? (data as { revoked_count: number }).revoked_count
          : 0;
      return count;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  return {
    revokeOneAsync: async (sid: string): Promise<void> => {
      await revokeOne.mutateAsync(sid);
    },
    revokeAllAsync: async (): Promise<number> => revokeAll.mutateAsync(undefined),
    isPendingOne: revokeOne.isPending,
    isPendingAll: revokeAll.isPending,
  };
}
